/**
 * O hub do turno e os agentes que responderam — slice COMPARTILHADO pelas quatro
 * superfícies de chat (D6 da spec-010 `supervisor-e-agentes-de-dominio`, [T028];
 * RF-G2 do verdict de review front).
 *
 * ## Por que ele saiu do namespace principal
 *
 * `apiChat.js` despacha os três setters pelo namespace de QUEM o instanciou
 * (`this.storeNamespace + '/setHubDaConversa'`, e as duas irmãs) — e o
 * `APIChat` é estendido por todas as superfícies: o chat do CRM
 * (`aawz-intelligence-v2`), o drawer do Especialista Fee-Based (`feeFixoIa`), o
 * chat de agente do `ai-framework` (`aiFramework`) e a aba de IA do módulo de
 * comissões (`comissoesIa`). Enquanto as actions moraram só no store do módulo
 * principal, as demais superfícies respondiam `[vuex] unknown action type` a
 * CADA turno — sem quebrar o streaming, sem estourar teste, e sem badge de
 * agente nenhum na tela.
 *
 * Medido em 2026-09-04 no app real (drawer do Especialista sobre
 * `/fee-fixo/clientes`): três `unknown action type` por turno
 * (`feeFixoIa/setAgentesDoTurnoAoVivo`, `feeFixoIa/setHubDaConversa`,
 * `feeFixoIa/setAgenteDaMensagem`).
 *
 * ## O mesmo movimento do `toolParts.js`
 *
 * É o slice irmão, e pelo mesmo motivo: o que o motor de chat despacha por
 * namespace tem de existir em TODO namespace que o instancia. Copiar as três
 * actions para cada store resolveria o turno de hoje e divergiria no primeiro
 * campo novo — a lista de agentes do fan-out (`agentes`) já nasceu assim, como
 * campo ADITIVO dentro da entrada. Uma fonte, quatro composições.
 *
 * ★ **O state nasce de FUNÇÃO** (`criarStateHubEAgente`), nunca de objeto
 * literal compartilhado: `agentePorMensagem` é um mapa, e um mapa compartilhado
 * faria o badge de uma mensagem do chat do CRM aparecer sobre a resposta do
 * drawer do Fee Fixo — o mesmo modo de falha que o `criarStateToolParts()`
 * evita nas partes de tool.
 *
 * ⚠️ Quem trava a junção é `superficies-do-chat.unit.js`: ele varre os
 * dispatches namespaced do motor e exige que cada store de superfície os
 * exponha. Slice novo que o `apiChat` despache entra naquela guarda sozinho.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */

/**
 * O estado das três fatias, sempre NOVO.
 *
 * - `hubDaConversa`: hub (`profissional`|`gestao`|`servicos`) do turno/conversa
 *   CORRENTE. Alimentado por `start` (turno pinado, hub sabido antes do grafo
 *   rodar) e por `agent-selected` (turno humano — só existe DEPOIS de
 *   `route_domains`). `null` = ainda não roteado, ou turno do motor legado sem
 *   os campos novos; `agent-badge.vue` cai no estado `carregando`/`vazio`, que
 *   NÃO é a mesma coisa que `HUB_PROFISSIONAL` (esse seria afirmar um hub que
 *   ninguém confirmou).
 * - `agentePorMensagem`: mapa `{ [id da mensagem]: { agentSlug, hub, motivo,
 *   agentes } }` — o agente que respondeu CADA mensagem do assistente, para o
 *   badge por resposta (`contracts/ui-components.md` §1.1). A chave é o `id`
 *   que o `end` devolve, e por isso o commit só acontece no `_onEventEnd`,
 *   nunca ao vivo (o `agent-selected` chega ANTES de a mensagem existir).
 * - `agentesDoTurnoAoVivo`: espelho AO VIVO de `agentes` para o turno EM
 *   STREAMING (RF-G2) — a mensagem ainda não tem `id`, então não dá para
 *   indexar `agentePorMensagem`. Zerado em cada `start` e em cada `end`: o
 *   turno seguinte não pode herdar os badges do anterior.
 */
/**
 * spec-009 [T008] — o agente AO VIVO (`agentesDoTurnoAoVivo[0]`), ou `null`
 * quando a entrada é vazia (`agentSlug`/`hub` ausentes). Função de módulo
 * para manter a complexidade do getter sob o teto do lint.
 */
function agenteAoVivo(state) {
  const entrada = (state.agentesDoTurnoAoVivo || [])[0]
  if (!entrada || (!entrada.agentSlug && !entrada.hub)) return null
  return {
    agentSlug: entrada.agentSlug || '',
    hub: entrada.hub || null,
    motivo: entrada.motivo || '',
  }
}

/**
 * O agente da ÚLTIMA mensagem `role: 'assistant'` do histórico
 * (`state.messages`), ou `null` quando não há uma, ou quando a última não
 * traz `agent_slug`/`hub` (turno do motor legado — AC-4.5, nunca inventa hub).
 */
function agenteDaUltimaMensagemAssistant(mensagens) {
  for (let i = mensagens.length - 1; i >= 0; i -= 1) {
    const mensagem = mensagens[i]
    if (!mensagem || mensagem.role !== 'assistant') continue
    if (!mensagem.agent_slug && !mensagem.hub) return null
    return {
      agentSlug: mensagem.agent_slug || '',
      hub: mensagem.hub || null,
      motivo: '',
    }
  }
  return null
}

export function criarStateHubEAgente() {
  return {
    hubDaConversa: null,
    agentePorMensagem: {},
    agentesDoTurnoAoVivo: [],
  }
}

export const hubEAgenteMutations = {
  // D6 (spec-010, [T028]) — hub do turno/conversa corrente.
  SET_HUB_DA_CONVERSA(state, payload) {
    state.hubDaConversa = payload || null
  },

  /**
   * Upsert de UMA entrada no mapa `agentePorMensagem`, pela mesma chave (`id`)
   * que a mensagem persistida usa. Substitui o objeto inteiro (não muta a
   * entrada existente), mesma disciplina de `UPSERT_FOLDER` — reatividade do
   * Vue 2 em objeto raso.
   *
   * RF-G2 — `agentes` é o array COMPLETO do fan-out; default
   * `[{agentSlug, hub, motivo}]` quando o payload não traz a chave (dispatcher
   * antigo, ou teste que só monta o topo), para que a entrada nunca fique sem
   * pelo menos 1 badge para quem já lê `agentes`.
   */
  SET_AGENTE_DA_MENSAGEM(state, payload) {
    const id = payload && payload.id
    if (!id) return
    const agentSlug = payload.agentSlug || ''
    const hub = payload.hub || null
    const motivo = payload.motivo || ''
    const agentes = payload.agentes && payload.agentes.length
      ? payload.agentes
      : [{ agentSlug, hub, motivo }]
    state.agentePorMensagem = {
      ...state.agentePorMensagem,
      [id]: { agentSlug, hub, motivo, agentes },
    }
  },

  // RF-G2 — lista AO VIVO dos agentes do turno em streaming. Setter puro,
  // substitui o array inteiro (nunca muta): quem decide o conteúdo é o
  // `apiChat.js`, nunca a mutation.
  SET_AGENTES_DO_TURNO_AO_VIVO(state, payload) {
    state.agentesDoTurnoAoVivo = payload || []
  },
}

export const hubEAgenteGetters = {
  /**
   * Hub do turno/conversa corrente. `null` é estado legítimo (ainda não
   * roteado, ou motor legado sem os campos novos): o consumidor NUNCA deve
   * tratar `null` como `HUB_PROFISSIONAL` por conta própria — quem resolve o
   * fallback é `constants/Hubs.js::getHub`.
   */
  hubDaConversa: (state) => state.hubDaConversa || null,

  // Mapa `{ [idDaMensagem]: {agentSlug, hub, motivo, agentes} }`. Objeto sempre
  // presente (nunca `undefined`), convenção dos getters destes stores.
  agentePorMensagem: (state) => state.agentePorMensagem || {},

  /**
   * O agente de UMA mensagem — `null` quando a mensagem não tem entrada no mapa
   * (turno antigo sem `agent_slug`, ou id ausente). Getter derivado (função de
   * função) para o badge não precisar ler o mapa inteiro por mensagem.
   */
  agenteDaMensagem: (state) => {
    const mapa = state.agentePorMensagem || {}
    return (id) => (id && mapa[id]) || null
  },

  /**
   * RF-G2 — TODOS os agentes de UMA mensagem (fan-out completo, na ordem de
   * chegada). Default `[]` (nunca `undefined`): mensagem sem entrada no mapa
   * (turno antigo sem `agent_slug`) devolve lista vazia, e quem consome cai no
   * estado `vazio` (só o hub, sem badge de agente nenhum).
   */
  agentesDaMensagem: (state) => {
    const mapa = state.agentePorMensagem || {}
    return (id) => {
      const entrada = id && mapa[id]
      return (entrada && entrada.agentes) || []
    }
  },

  /**
   * RF-G2 — lista AO VIVO dos agentes do turno em streaming
   * (`apiChat._onAgentSelected`/`_onEventStart` atualizam a cada chegada).
   * Default `[]`: turno recém-iniciado (ou legado, sem `agent-selected`) ainda
   * não tem nenhum badge de agente resolvido.
   */
  agentesDoTurnoAoVivo: (state) => state.agentesDoTurnoAoVivo || [],

  /**
   * spec-009 [T008] — o agente que está (ou acabou de) respondendo NESTA
   * conversa, para o badge ao lado do "Cliente em foco"
   * (`contracts/ui-components.md` §4). `{agentSlug, hub, motivo}` ou `null`.
   *
   * Precedência (AC-4.1/AC-4.2): AO VIVO (`agentesDoTurnoAoVivo[0]`, o
   * fan-out do turno em streaming) vence o HISTÓRICO — a mesma regra de
   * `_agenteDoTurno` em `apiChat.js`. Sem turno em voo, cai na ÚLTIMA
   * mensagem `role: 'assistant'` do histórico carregado
   * (`contracts/api.yaml`: o histórico devolve `agent_slug`/`hub` POR
   * MENSAGEM, resolvidos pelo backend via `id_run → runs.agent_slug →
   * agentes.hub`).
   *
   * ★ Lê `state.messages` DIRETO, não `agentePorMensagem` — aquele mapa só é
   * escrito no `_onEventEnd` do turno AO VIVO (chave = `id` que a mensagem
   * ainda não tem ao carregar histórico); usar mensagens já sincroniza a
   * régua com o que o backend devolve por mensagem, sem exigir hidratação
   * própria neste getter.
   *
   * `null` (AC-4.5) quando: não há turno ao vivo, não há mensagem
   * `assistant`, OU a última tem `agent_slug`/`hub` ausentes (turno do motor
   * legado, ou mensagem sem `id_run`) — o consumidor NUNCA deve inventar um
   * hub que ninguém confirmou.
   */
  agenteAtualDaConversa: (state) =>
    agenteAoVivo(state) || agenteDaUltimaMensagemAssistant(state.messages || []),
}

// Actions são setters puros — quem decide o conteúdo é o parser SSE
// (`apiChat.js`), nunca a action. Mesma regra do slice `toolParts`.
export const hubEAgenteActions = {
  setHubDaConversa({ commit }, payload) {
    commit('SET_HUB_DA_CONVERSA', payload)
  },
  setAgenteDaMensagem({ commit }, payload) {
    commit('SET_AGENTE_DA_MENSAGEM', payload)
  },
  setAgentesDoTurnoAoVivo({ commit }, payload) {
    commit('SET_AGENTES_DO_TURNO_AO_VIVO', payload)
  },
}

// Módulo autocontido, para quem preferir registrar o slice namespaced
// (`<ns>/hubEAgente/*`) em vez de compor no namespace raiz. `state` é função:
// registrar o MESMO objeto em dois stores continua dando dois estados.
export default {
  namespaced: true,
  state: criarStateHubEAgente,
  getters: hubEAgenteGetters,
  mutations: hubEAgenteMutations,
  actions: hubEAgenteActions,
}
